/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, September 21st 2022, 9:55:59 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { FieldDependency } from "lib/schemas";
import { getDefaultFieldDependency } from "lib/factories";
import { useFieldDependenciesAPI } from "form-builder/services/FormsAPI";
import { useFormBuilderStore, currentFormSelector } from "form-builder/stores";
import { FormIcon } from "theme/icons";
import { COLORSCHEME } from "theme/styles";
import { OSAPIFormProvider } from "ui-widgets/forms/providers";
import { OSLoadingButton, OSActionButton, StyledOSDialog, ActionDialog, OSAlert } from "ui-widgets";
import { FieldDependencyFormConditions } from "./FieldDependencyFormConditions";
import { FieldDependencyFormActions } from "./FieldDependencyFormActions";
import { FieldDependencyLogicTypeSelector } from "./FieldDependencyLogicTypeSelector";

type FieldDependencyFormModalProps = {
  fieldDependencyId?: FieldDependency["id"];
  handleClose: () => void;
  open: boolean;
};

export const FieldDependencyFormModal = (props: FieldDependencyFormModalProps) => {
  const {
    showDiscardConfirmation,
    handleCloseDiscardChanges,
    handleDiscardChanges,
    onCloseFormDialog,
    setHasUnsavedChanges,
  } = useFieldDependencyFormModal(props);

  return (
    <StyledOSDialog
      open={props.open}
      popUpTitle="Field Dependency"
      popUpIcon={<FormIcon fill={COLORSCHEME.secondary} />}
      onClose={onCloseFormDialog}
    >
      <FieldDependencyForm
        fieldDependencyId={props.fieldDependencyId}
        onSave={props.handleClose}
        setHasUnsavedChanges={setHasUnsavedChanges}
      />
      <ActionDialog
        severity="warning"
        title={"Discard Changes"}
        open={showDiscardConfirmation}
        onClose={handleCloseDiscardChanges}
        confirmationAction={handleDiscardChanges}
        dialogMessage={
          <>
            <p>Are you sure you want to discard all the changes made to this Field Dependency?</p>
            <p> You will lose your changes unless you click the save button.</p>
          </>
        }
        renderActions={() => (
          <>
            <OSActionButton size="large" variant="contained" onClick={handleDiscardChanges}>
              Discard
            </OSActionButton>
            <OSActionButton size="large" variant="text" onClick={handleCloseDiscardChanges}>
              Cancel
            </OSActionButton>
          </>
        )}
      />
    </StyledOSDialog>
  );
};

const useFieldDependencyFormModal = ({ open, handleClose, fieldDependencyId }: FieldDependencyFormModalProps) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = React.useState(false);

  const handleCloseDiscardChanges = () => {
    setShowDiscardConfirmation(false);
  };

  const handleDiscardChanges = () => {
    setShowDiscardConfirmation(false);
    handleClose();
  };

  const onCloseFormDialog = () => {
    if (hasUnsavedChanges) setShowDiscardConfirmation(hasUnsavedChanges);
    else handleClose();
  };

  return {
    showDiscardConfirmation,
    handleCloseDiscardChanges,
    handleDiscardChanges,
    onCloseFormDialog,
    setHasUnsavedChanges,
  };
};

type FieldDependencyFormProps = {
  fieldDependencyId?: FieldDependency["id"];
  onSave?: () => void;
  setHasUnsavedChanges?: (hasUnsavedChanges: boolean) => void;
};

const FieldDependencyForm = (props: FieldDependencyFormProps) => {
  const {
    fieldDependency,
    useAPIFormReturn,
    isAPIDataSendError,
    isSendingDataToAPI,
    conditionLogicType,
    onChangeConditionLogicType,
    submitAPIForm,
  } = useFieldDependencyForm(props);

  return (
    <OSAPIFormProvider useAPIFormReturn={useAPIFormReturn}>
      <Stack className="dependency-edit-card" spacing={2}>
        {isAPIDataSendError && (
          <OSAlert severity="error" variant="filled">
            The field dependency could not be saved. Please provide all the required fields.
          </OSAlert>
        )}
        <Paper elevation={5}>
          <FieldDependencyLogicTypeSelector
            prefix="Apply dependency if"
            conditionLogicType={conditionLogicType}
            onChangeConditionLogicType={onChangeConditionLogicType}
          />
        </Paper>
        <Paper elevation={5} className="dependency-conditions">
          <FieldDependencyFormConditions fieldDependency={fieldDependency} />
        </Paper>
        <Paper elevation={5} className="dependency-actions">
          <FieldDependencyFormActions fieldDependency={fieldDependency} />
        </Paper>
        <OSLoadingButton
          isLoading={isSendingDataToAPI}
          variant="contained"
          size="large"
          className="save-button"
          onClick={submitAPIForm}
        >
          Save
        </OSLoadingButton>
      </Stack>
    </OSAPIFormProvider>
  );
};

const useFieldDependencyForm = ({ fieldDependencyId, onSave, setHasUnsavedChanges }: FieldDependencyFormProps) => {
  const { useFieldDependenciesAPIForm } = useFieldDependenciesAPI();
  const addFieldDependency = useFormBuilderStore((state) => state.addFieldDependency);
  const currentFormConfiguration = useFormBuilderStore(currentFormSelector);
  const fieldDependency = useFormBuilderStore(
    (state) =>
      state.fieldDependencies.find((fieldDep) => fieldDep.id === fieldDependencyId) ??
      getDefaultFieldDependency({ form: currentFormConfiguration })
  );
  const onSaveFieldDependency = (fieldDependency: FieldDependency) => {
    addFieldDependency(fieldDependency);
    onSave?.();
  };
  const useAPIFormReturn = useFieldDependenciesAPIForm({
    fieldDependency,
    updateFieldDependencyFromAPICallback: onSaveFieldDependency,
  });
  const {
    useFormReturn: {
      watch,
      setValue,
      formState: { isDirty, isSubmitted },
    },
    submitAPIForm,
    isAPIDataSendError,
    isSendingDataToAPI,
  } = useAPIFormReturn;

  React.useEffect(() => {
    setHasUnsavedChanges?.(isDirty && !isSubmitted);
  }, [setHasUnsavedChanges, isDirty, isSubmitted]);

  const conditionLogicType = watch("condition_logic_type");

  const onChangeDependencyValue = (key: keyof FieldDependency, value: any) => {
    setValue(key, value, { shouldDirty: true });
  };

  return {
    fieldDependency,
    useAPIFormReturn,
    isAPIDataSendError,
    isSendingDataToAPI,
    conditionLogicType,
    onChangeConditionLogicType: (chosenValue) => onChangeDependencyValue("condition_logic_type", chosenValue),
    submitAPIForm,
  };
};
